'use client';

import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { Card } from '../../../../../components/card';
import { MarkdownPreview } from '../../../../markdown-preview';
import { Bug01Icon, File01Icon, SparklesIcon } from '@terra/ui-icons';
import type { FindingDescriptionData } from '../types';

type FindingDescriptionHITLRendererProps = {
  findings: FindingDescriptionData[];
  maxHeight?: string;
  variant?: 'compact' | 'detailed';
};

export function FindingDescriptionHITLRenderer({
  findings,
  maxHeight = '400px',
  variant = 'detailed',
}: FindingDescriptionHITLRendererProps) {
  const tHitl = useTranslations('designSystem.chat.findingDescriptionHitl');
  const tModal = useTranslations(
    'designSystem.chat.editFindingDescriptionModal',
  );
  const isDetailed = variant === 'detailed';

  if (!findings || findings.length === 0) {
    return null;
  }

  return (
    <VStack alignItems="start" gap={1.5} width="100%">
      <VStack
        gap={isDetailed ? 3 : 1.5}
        width="100%"
        maxHeight={maxHeight}
        overflowY="auto"
        overflowX="hidden"
        alignItems="stretch"
      >
        {findings.map((finding, index) => (
          <Card.Root key={index} flexShrink={0}>
            <Card.Body p={isDetailed ? 3 : 2.5}>
              <VStack
                alignItems="start"
                gap={isDetailed ? 3 : 1.5}
                width="100%"
              >
                <HStack
                  alignItems="center"
                  gap={1.5}
                  flexWrap="wrap"
                  width="100%"
                  justifyContent="space-between"
                >
                  <HStack gap={1.5} flexWrap="wrap">
                    <Badge
                      size="sm"
                      variant="emphasis"
                      icon={<Bug01Icon size={12} />}
                    >
                      {tHitl('securityFinding')}
                    </Badge>

                    <Badge
                      size="sm"
                      variant="outline"
                      icon={<SparklesIcon size={10} />}
                    >
                      {tHitl('aiGenerated')}
                    </Badge>
                  </HStack>
                </HStack>

                {isDetailed && (
                  <HStack gap={1} alignItems="center">
                    <File01Icon size={14} color="var(--colors-fg-muted)" />
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="fg.muted"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      {tModal('fields.findingDescription')}
                    </Text>
                  </HStack>
                )}

                {finding.description && (
                  <Box
                    width="100%"
                    bg="bg.subtle"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.subtle"
                    overflow="hidden"
                  >
                    <Box p={isDetailed ? 3 : 2}>
                      <MarkdownPreview
                        source={finding.description}
                        compact={!isDetailed}
                      />
                    </Box>
                  </Box>
                )}

                {isDetailed && finding.description && (
                  <HStack gap={1} alignItems="center" width="100%">
                    <Text fontSize="2xs" color="fg.subtle">
                      {tModal('wordCount', {
                        wordCount: finding.description.split(/\s+/).length,
                        charCount: finding.description.length,
                      })}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
        ))}
      </VStack>
    </VStack>
  );
}
